/* eslint-disable no-console */
import { Assets, AssetsBundle } from 'pixi.js';
import { pascalToKebab } from './case-transformer';
import { raise } from './exceptions';

const debug = false;

export type Class = new (...args: any[]) => any;
export type AssetBundle = { id: string; assets: AssetsBundle['assets']; source?: Class };
export type ClassWithAssets = Class & { assets: AssetBundle & { dependencies?: ClassWithAssets[] } };

export const getAssetBundleId = (target: Class) => pascalToKebab(target.name);
export const getAssetBundle = (target: Class): AssetBundle =>
  isAssetsClass(target) ? target.assets : raise('no assets metadata found for ' + target.name);
export const isAssetsClass = (obj: any): obj is ClassWithAssets =>
  typeof obj === 'function' && 'assets' in obj && 'id' in obj.assets && 'assets' in obj.assets;
export const isAssetBundle = (obj: any): obj is AssetBundle => 'id' in obj && 'assets' in obj;
export const isClass = (obj: any): obj is Class => typeof obj === 'function';

export const assets = (name: string, assets: AssetsBundle['assets'], dependencies?: Class[]) => {
  return (target: Class) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any
    (target as any).assets = {
      id: name === 'auto' || name === '' ? getAssetBundleId(target) : name,
      assets,
      source: target,
      dependencies,
    };
  };
};

export function* collectAssetBundles(target: Class): Generator<AssetBundle> {
  if (!isAssetsClass(target)) throw new Error('no assets metadata found for ' + target.name);
  yield target.assets;
  if (!target.assets.dependencies) return;
  for (const dependency of target.assets.dependencies) {
    yield* collectAssetBundles(dependency);
  }
}

export function loadAssetBundles(targets: (Class | AssetBundle)[], onProgress?: (p: number) => void) {
  const bundles = targets.flatMap((target) => (isClass(target) ? [...collectAssetBundles(target)] : target));
  bundles.forEach((bundle) => Assets.addBundle(bundle.id, bundle.assets));
  if (debug) {
    bundles.forEach((bundle) => {
      console.groupCollapsed('add load bundle', bundle.id, bundle.source?.name);
      console.log(bundle.assets);
      console.groupEnd();
    });
  }
  return Assets.loadBundle(
    bundles.map((bundle) => bundle.id),
    onProgress
  ).then(() => {
    if (debug) console.log('>>>> loadAssetBundles', Assets);
  });
}

// TODO: it does not support progress for now, removed until it does
// export function unloadAssetBundles<T>(targets: Iterable<Class>, onProgress?: (p: number) => void): Promise<any[]> {
export function unloadAssetBundles(targets: (Class | AssetBundle)[]) {
  const bundles = targets.flatMap((target) => (isClass(target) ? [...collectAssetBundles(target)] : target));

  if (debug) {
    bundles.forEach((bundle) => {
      console.groupCollapsed('add unload bundle', bundle.id, bundle.source?.name);
      console.log(bundle.assets);
      console.groupEnd();
    });
  }
  return Assets.unloadBundle(bundles.map((bundle) => bundle.id)).then(() => {
    if (debug) console.log('>>>> unloadAssetBundles', Assets);
  });
}
